import { Icon } from "./Common.jsx";
import { coachEnabled } from "../lib/coachEnabled.js";
import { adaptiveParams, gameState as adaptiveGameState, avgScoreLastMonth, gameInsight, levelWord, startingTarget, trendPoints } from "../lib/adaptive.js";
import { cmsConfig, t as ct } from "../lib/content.js";
import { useBackDepth, useBackLayer } from "../lib/backstack.js";
import { effectiveMathsBands, resolveMathsBand, mathsBandTypeIds, mathsItemGrade, TT_SESSION_SIZE, ttLevelPhrase } from "../lib/maths.js";
import { effectiveVocabCategories, resolveVocabCategory } from "../lib/vocab.js";
import { customTestsSince, engineLog, scoredLog, testsInWeek, weekBefore } from "../lib/coach-custom.js";
import { MAX_TEST_SIZE, MIN_TEST_SIZE, clampTestSize, masteredWordCount, wordCounts } from "../lib/coach-words.js";
import { DEFAULT_COACH_TEST_SIZE, DEFAULT_PASS_THRESHOLD } from "../lib/constants.js";
import { effectiveSpellingLists, resolveSpellingList, spellingListItems } from "../lib/spelling.js";
import { resolveSchoolYear } from "../lib/age.js";
import { mathsTypeLabel, spellingTopicLabel } from "../lib/adaptive-labels.js";
import { kidLevelLabel, comingUpNext } from "./ChildView.jsx";
import { LearningAdminTab, Stepper } from "./LearningAdminTab.jsx";
import { todayStr, weekStartOf } from "../lib/utils.js";
import { ParentTestDetail, ParentTestHistory, RecentTestsCard, TestHistoryEntry, historyGameName } from "./TestHistory.jsx";
import { HomeworkCreate, HomeworkListPage, HomeworkPage, HomeworkRow } from "./HomeworkPractice.jsx";
import { canAddList, findHomeworkList, homeworkUnlocked } from "../lib/homework.js";

const { useEffect, useRef, useState } = React;

// Learn & Earn hub (LB-19 Zip B, drawings v3 signed off) — the parent's whole route into
// Learn & Earn from the Home quick action: child picker (screen 1) → Adaptive summary
// (screen 2) → per-game detail (2b) with the switch-to-manual warning sheet (2c), or the
// Manual view (LearningAdminTab) for a manual child / an overridden game. "Override" here
// IS the existing manual mechanism (a concrete list/band/level freezes Adaptive for that
// game — see LearnEarnTab's spellingManual/vocabManual/mathsManual), so nothing new is
// stored: Switch to manual writes the working band into the game's own setting, Back to
// Adaptive writes "auto". Times tables have no band, so no override state — their Change
// opens the Manual tables setting directly (the draw adapts within whatever is chosen).

export const HUB_GAMES = ["spelling", "vocab", "mathsworkout", "ttstreaks"];
export const HUB_GAME_NAMES = { spelling: "Spelling", vocab: "Vocabulary", mathsworkout: "Maths", ttstreaks: "Times tables" };

// One resolved view of a child's Adaptive standing per game — the single source the picker,
// summary and detail all read, so the three screens never disagree.
export function childGameView(child, learning, householdAdaptiveOn) {
  const l = learning || {};
  const adaptive = l.adaptive || {};
  const out = {};
  const mk = (game, { manual, byAge, bandLabels }) => {
    const st = adaptiveGameState(adaptive, game, byAge);
    if (!adaptive[game]) st.target = startingTarget(game, child);
    const active = householdAdaptiveOn && !manual && byAge !== "off" && byAge !== "custom";
    const band = st.band && bandLabels && bandLabels[st.band] ? st.band : byAge;
    out[game] = { st, active, manual: !!manual && householdAdaptiveOn, band, bandLabel: bandLabels && bandLabels[band] ? bandLabels[band].label || band : null };
  };
  mk("spelling", { manual: l.spellingAuto?.list && l.spellingAuto.list !== "auto", byAge: resolveSpellingList(child, (l.spellingAuto || {}).list), bandLabels: effectiveSpellingLists() });
  mk("vocab", { manual: l.vocabCategory && l.vocabCategory !== "auto", byAge: resolveVocabCategory(child, l.vocabCategory), bandLabels: effectiveVocabCategories() });
  mk("mathsworkout", { manual: l.mathsBand && l.mathsBand !== "auto", byAge: resolveMathsBand(child, l.mathsBand), bandLabels: effectiveMathsBands() });
  mk("ttstreaks", { manual: false, byAge: null, bandLabels: null });
  return out;
}

// The score trend: the last six scored tests, 0–100. Alan, 28 Sep: a run of full marks drew as
// a bare line along the top edge and read as a stray mark, not a chart. It now sits on a faint
// track (the 0–100 box, with its floor) and ends in a dot, so "flat at the top" looks like what
// it is. One test is a dot on its own; none draws nothing.
function Sparkline({ points, tone }) {
  if (!points || points.length < 1) return null;
  const w = 72, h = 28, pad = 3;
  const y = (p) => h - pad - ((h - 2 * pad) * Math.max(0, Math.min(100, p))) / 100;
  const xs = points.length === 1 ? [w / 2] : points.map((_, i) => pad + (i * (w - 2 * pad)) / (points.length - 1));
  const ys = points.map(y);
  const d = xs.map((x, i) => `${Math.round(x)},${Math.round(ys[i])}`).join(" ");
  const stroke = tone === "up" ? "#0E7A3C" : tone === "flat" ? "#B07E00" : "#9AA3B5";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="flex-shrink-0" data-testid="adaptive-sparkline" data-points={points.length}>
      <rect x="0.5" y="0.5" width={w - 1} height={h - 1} rx="3" fill="#F3F5F9" stroke="#E3E8F0" />
      <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="#D9DEE8" strokeWidth="1" />
      {points.length > 1 && <polyline points={d} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
      <circle cx={Math.round(xs[xs.length - 1])} cy={Math.round(ys[ys.length - 1])} r="2.5" fill={stroke} />
    </svg>
  );
}

function trendTone(points) {
  if (!points || points.length < 2) return "flat";
  const half = Math.floor(points.length / 2);
  const a = points.slice(0, half).reduce((x, y) => x + y, 0) / half;
  const b = points.slice(half).reduce((x, y) => x + y, 0) / (points.length - half);
  return b - a >= 4 ? "up" : b - a <= -8 ? "down" : "flat";
}

function gameLevelText(game, view) {
  // Times tables have no band, so they show the level WORD on its own — never "Level 5 of 5"
  // beside a kid screen that says Champion (12 Sep staging pass).
  // Tables name the tables in play rather than a level word: the target picks WHICH TABLES
  // (2,5,10 → 6,9 → 7,8 → 11,12), so "Champion" was describing content as attainment.
  if (game === "ttstreaks") return ttLevelPhrase(view.st.target);
  const label = kidLevelLabel(game, view.st);
  if (label) return label;
  return view.bandLabel ? `${view.bandLabel} · ${levelWord(view.st.target)}` : levelWord(view.st.target);
}

function topicLabelFor(game) {
  if (game === "spelling") return spellingTopicLabel;
  if (game === "mathsworkout" || game === "ttstreaks") return mathsTypeLabel;
  return (t) => String(t || "").replace(/^.*:g/, "grade ");
}

// ---- screen 1: child picker -------------------------------------------------------------
// The Learning Coach advert. One component, because the two entry points (the child picker
// from Parent Home, and the "Set it yourself" page a Kids-tab family lands on when Coach is
// off) must show the same thing — a second copy would drift the way the two journeys did.
export function CoachAdvert({ onOpen }) {
  return (
    <button onClick={onOpen} className="rounded-xl px-4 py-3.5 text-left flex flex-col gap-1.5" style={{ background: "#1B2A4A" }} data-testid="le-picker-advert">
      <span className="inline-flex items-center gap-2 self-start">
        <span className="text-[10px] font-extrabold uppercase tracking-wide bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">{ct("adaptive.hub.free_trial", "Free in the Preview")}</span>
      </span>
      <span className="block text-sm font-extrabold text-white leading-snug">{ct("adaptive.hub.advert_title", "Let us find the right level of challenge for your child")}</span>
      <span className="block text-xs text-white/90">{ct("adaptive.hub.advert_body", "Learning Coach sets the difficulty for each game based on your child's performance and manages it constantly, so tests are challenging, not overwhelming.")}</span>
      <span className="flex items-center gap-1 text-[12px] font-extrabold text-white mt-1.5">
        {ct("adaptive.hub.advert_cta", "See how it works")}
        <Icon id="ic-forward" size={14} className="text-white/80" style={{ color: "currentColor" }} />
      </span>
    </button>
  );
}

export function LearnEarnChildPicker({ state, onOpenChild, onOpenManual, onOpenExplainer }) {
  const householdOn = coachEnabled(state.settings);
  return (
    <div className="flex flex-col gap-3" data-testid="le-child-picker">
      <div className="bg-white rounded-lg overflow-hidden">
        {(state.profiles || []).map((child) => {
          const learning = (state.learningContent || {})[child.id] || {};
          const views = childGameView(child, learning, householdOn);
          const anyAdaptive = HUB_GAMES.some((g) => views[g].active);
          return (
            <button key={child.id} onClick={() => (anyAdaptive ? onOpenChild(child.id) : onOpenManual(child.id))} className="w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-100 last:border-b-0" data-testid={`le-picker-row-${child.id}`}>
              <span className="w-9 h-9 rounded-full bg-indigo-600 text-white font-extrabold flex items-center justify-center flex-shrink-0 mt-0.5">{(child.name || "?").slice(0, 1)}</span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 text-sm font-bold text-slate-900">{child.name}
                  {anyAdaptive && <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700">{ct("adaptive.hub.chip", "Learning Coach")}</span>}
                </span>
                {anyAdaptive ? (
                  <span className="block mt-1" data-testid={`le-picker-levels-${child.id}`}>
                    {HUB_GAMES.filter((g) => views[g].active || views[g].manual).map((g) => (
                      <span key={g} className="flex justify-between text-xs text-slate-500 py-0.5">
                        <span>{HUB_GAME_NAMES[g]}</span>
                        <span className="font-bold text-slate-800">{views[g].manual ? ct("adaptive.hub.row_manual", "Set by you") : gameLevelText(g, views[g])}</span>
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="block text-xs text-slate-500 mt-0.5">{ct("adaptive.hub.manual_sub", "Manual: you choose the difficulty levels and set the tests")}</span>
                )}
              </span>
              <Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0 mt-1" style={{ color: "currentColor" }} />
            </button>
          );
        })}
      </div>
      {(state.profiles || []).some((child) => {
        const learning = (state.learningContent || {})[child.id] || {};
        const views = childGameView(child, learning, householdOn);
        return !HUB_GAMES.some((g) => views[g].active);
      }) ? (
        <CoachAdvert onOpen={onOpenExplainer} />
      ) : null}
    </div>
  );
}

// ---- screen 2: Adaptive summary ---------------------------------------------------------
export function AdaptiveSummaryPage({ state, childId, onSwitchChild, onOpenGame, onBackToAdaptive, onOpenHistory = null, hideSwitcher = false, homeworkRow = null }) {
  const householdOn = coachEnabled(state.settings);
  const today = todayStr();
  const params = adaptiveParams(cmsConfig("adaptive.params"));
  const child = (state.profiles || []).find((p) => p.id === childId);
  if (!child) return null;
  const learning = (state.learningContent || {})[child.id] || {};
  const views = childGameView(child, learning, householdOn);
  const others = (state.profiles || []).filter((p) => {
    const l = (state.learningContent || {})[p.id] || {};
    const v = childGameView(p, l, householdOn);
    return HUB_GAMES.some((g) => v[g].active);
  });
  return (
    <div className="flex flex-col gap-3" data-testid="adaptive-summary">
      {!hideSwitcher && others.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {others.map((p) => (
            <button key={p.id} onClick={() => onSwitchChild(p.id)} className={`text-xs font-bold rounded-full px-3 py-1.5 ${p.id === childId ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>{p.name}</button>
          ))}
        </div>
      )}
      {/* LB-366 (Alan, 23 Sep): the whole history is reachable before a game is picked. */}
      {onOpenHistory && <TestHistoryEntry childName={child.name} onOpen={() => onOpenHistory(child.id)} />}
      {/* LB-364: homework practice sits beside Coach in the same row shape, above the games. */}
      {homeworkRow}
      <div className="bg-white rounded-lg overflow-hidden">
        {HUB_GAMES.filter((g) => views[g].active || views[g].manual).map((g) => {
          const v = views[g];
          // LB-291 (Alan, 15 Sep). A set-by-you row used to be the one row that did not open
          // the detail — its only affordance handed control back. But LB-278 deliberately
          // keeps the tests a child takes on a parent's list, so the data existed and the way
          // to it did not. Now all four rows do the same thing when tapped (the 12 Sep
          // one-shape rule), and the hand-back lives on the detail page next to the note that
          // explains what handing back means.
          // LB-365 ("only showing what the kid did last week"): the row counts the CURRENT
          // Monday-start week first, with last week alongside for the comparison a parent is
          // actually making. Stopped-early tests are counted here, not labelled — the labels
          // live on the Recent tests rows in the detail.
          const thisWeekStart = weekStartOf(today);
          const testsThisWeek = testsInWeek(v.st, thisWeekStart);
          const testsLastWk = testsInWeek(v.st, weekBefore(thisWeekStart));
          // LB-278 (Alan: "counts as a test but is marked as custom"). Without this a parent
          // reads the score beside it as Coach's own verdict on their child, which is exactly
          // the confusion that surfaced this ticket. It trails the count rather than taking a
          // line of its own, so rows with and without custom tests still line up. Counted
          // over the same window the line shows: this week and last.
          const customTests = customTestsSince(v.st, weekBefore(thisWeekStart));
          const insight = gameInsight(v.st, today, { topicLabel: topicLabelFor(g), params });
          const points = trendPoints({ ...v.st, log: scoredLog(v.st) });
          return (
            <button key={g} onClick={() => onOpenGame(child.id, g)} className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3" data-testid={`adaptive-row-${g}`}>
              {/* 12 Sep: one shape on every row — game, then level, then the insight. The chip
                  used to sit inline with the name, so a long band name pushed it onto its own
                  line and no two rows lined up. */}
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-bold text-slate-900">{HUB_GAME_NAMES[g]}</span>
                <span className="block mt-1"><span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 whitespace-nowrap" data-testid={v.manual ? `adaptive-row-manual-${g}` : undefined}>{v.manual ? ct("adaptive.hub.row_manual", "Set by you") : gameLevelText(g, v)}</span></span>
                <span className="block text-xs text-slate-400 mt-1">{ct("adaptive.hub.tests_weeks", "{a} test{plural} this week · {b} last week", { a: testsThisWeek, plural: testsThisWeek === 1 ? "" : "s", b: testsLastWk })}{customTests > 0 && <span className="text-teal-700" data-testid={`adaptive-custom-${g}`}> {ct("adaptive.hub.tests_custom", "· {n} from your list", { n: customTests })}</span>}</span>
                {insight.text && <span className="block text-xs text-slate-500 mt-0.5" data-testid={`adaptive-insight-${g}`}><span className="font-bold text-slate-800">{insight.lead} </span>{insight.text}</span>}
              </span>
              <Sparkline points={points} tone={insight.state === "drifting" ? "down" : trendTone(points)} />
              {/* Reads as a link and carries a chevron, so it is clearly the way in (12 Sep:
                  "make Results and topics look clickable"). */}
              <span className="flex items-center gap-1 flex-shrink-0 text-xs font-bold text-slate-900">
                <span className="underline">{ct("adaptive.hub.see_more", "See progress")}</span>
                <Icon id="ic-forward" size={12} style={{ color: "currentColor" }} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- screens 2b + 2c: per-game detail + the switch-to-manual warning --------------------
export function AdaptiveGameDetail({ state, update, childId, game, showToast, onSwitchedToManual, onHandBack, onOpenTest = null, onSeeAllTests = null }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // P1b (signed off 22 Sep): the rewards live behind a one-row summary + bottom sheet, so a
  // parent reaches pass marks and token values from Coach without a trip into Set it
  // yourself. The sheet writes the SAME per-child fields the manual page and the tests
  // read, so the values apply to every test, Coach-run or not.
  const [rewardsOpen, setRewardsOpen] = useState(false);
  // LB-315 (missed in the first cut): both sheets are layers. And both are PORTALED to the
  // body (Alan, 30 Sep, screenshot): rendered inside the Learn & Earn window their fixed box
  // became the window — flush to its edges, the Keep Learning Coach button cut off by its
  // overflow. Same cause and same fix as KidDetail's focus windows.
  useBackLayer(confirmOpen, () => setConfirmOpen(false));
  useBackLayer(rewardsOpen, () => setRewardsOpen(false));
  // LB-280 (Alan, 14 Sep): "have a default test size number and allow parent to override
  // (without prompting a return to manual)", free-format rather than a set of presets. It
  // lives in Coach, precisely so it is not a manual setting: it writes
  // learningContent.coachTestSize, which nothing in the manual/Coach switch reads.
  //
  // LB-282: these two hooks sat BELOW the `if (!child) return null` guard further down, so a
  // falsy child made this component call one hook instead of three and React cannot recover
  // from that. Found by react-hooks/rules-of-hooks on its first run. They read childId, which
  // is a prop, rather than the resolved child, so they can live up here safely.
  const lcForChild = (state.learningContent || {})[childId] || {};
  const [sizeDraft, setSizeDraft] = useState(() => String(clampTestSize(lcForChild.coachTestSize, DEFAULT_COACH_TEST_SIZE)));
  useEffect(() => { setSizeDraft(String(clampTestSize(lcForChild.coachTestSize, DEFAULT_COACH_TEST_SIZE))); }, [lcForChild.coachTestSize]);
  const householdOn = coachEnabled(state.settings);
  const today = todayStr();
  const params = adaptiveParams(cmsConfig("adaptive.params"));
  const child = (state.profiles || []).find((p) => p.id === childId);
  if (!child) return null;
  const learning = (state.learningContent || {})[child.id] || {};
  const views = childGameView(child, learning, householdOn);
  const v = views[game];
  const st = v.st;
  const thisWeekStart = weekStartOf(today);
  const testsThisWeek = testsInWeek(st, thisWeekStart);
  const testsLastWk = testsInWeek(st, weekBefore(thisWeekStart));
  // The average reads the same scored tests as the trend beside it, so the two agree.
  const avg = avgScoreLastMonth({ ...st, log: scoredLog(st) }, today);
  const points = trendPoints({ ...st, log: scoredLog(st) });
  // Whether this child has actually played this game yet. The "coming up next" note claims to
  // be based on what they found hard, which is untrue until there is a log to base it on.
  const hasHistory = ((st.log || []).length > 0);
  const mastered = Object.values(st.topics || {}).filter((t) => t.mastered).length;
  const yearLabel = resolveSchoolYear(child);
  const startedFrom = yearLabel ? ct("adaptive.hub.started_year", "started from {year}", { year: String(yearLabel).replace(/^Y/, "Year ") }) : null;
  const items = game === "spelling" ? spellingListItems(v.band) : null;
  const label = topicLabelFor(game);
  const isMastered = (t) => !!(st.topics && st.topics[t] && st.topics[t].mastered);
  const target = Math.max(1, Math.min(5, st.target || 1));
  const topicPool = game === "spelling"
    ? [...new Set((items || []).filter((w) => w.topic && !isMastered(w.topic) && Number.isInteger(w.grade) && w.grade >= target).map((w) => w.topic))]
    : game === "mathsworkout" || game === "ttstreaks"
      ? mathsBandTypeIds(v.band).slice().sort((a, b) => mathsItemGrade(a, 2, v.band) - mathsItemGrade(b, 2, v.band)).filter((id) => !isMastered(id))
      : [];
  const upcoming = topicPool.slice(0, 3).map(label);
  if (!upcoming.length) { const one = comingUpNext(game, st, items); if (one) upcoming.push(one); }
  const solidWords = masteredWordCount(wordCounts(learning.coachWords, game));
  // The rewards behind the row + sheet above — read with the same `?? defaults` the tests
  // themselves use, so the line always shows what the next test will actually pay.
  const rw = game === "ttstreaks"
    ? (() => { const size = learning.ttSessionSize ?? TT_SESSION_SIZE; return { pass: learning.ttStreakPassReward ?? 3, full: learning.ttStreakFullReward ?? 5, streak: learning.ttStreakPassCount ?? Math.round(size * 0.8), size }; })()
    : game === "vocab"
      ? { pass: learning.vocabPassReward ?? 3, full: learning.vocabFullReward ?? 5, threshold: learning.vocabPassThreshold ?? DEFAULT_PASS_THRESHOLD, thresholdKey: "vocabPassThreshold", passKey: "vocabPassReward", fullKey: "vocabFullReward" }
      : game === "mathsworkout"
        ? { pass: learning.mathsPassReward ?? 3, full: learning.mathsFullReward ?? 5, threshold: learning.mathsPassThreshold ?? DEFAULT_PASS_THRESHOLD, thresholdKey: "mathsPassThreshold", passKey: "mathsPassReward", fullKey: "mathsFullReward" }
        : { pass: learning.spellingPassReward ?? 3, full: learning.spellingReward ?? 5, threshold: learning.spellingPassThreshold ?? DEFAULT_PASS_THRESHOLD, thresholdKey: "spellingPassThreshold", passKey: "spellingPassReward", fullKey: "spellingReward" };

  function setRewardField(key, n) {
    update((s2) => {
      const l = s2.learningContent[child.id] || (s2.learningContent[child.id] = {});
      l[key] = n;
      return s2;
    });
  }

  function commitTestSize() {
    const n = clampTestSize(sizeDraft, clampTestSize(learning.coachTestSize, DEFAULT_COACH_TEST_SIZE));
    setSizeDraft(String(n));
    update((s2) => {
      const l = s2.learningContent[child.id] || (s2.learningContent[child.id] = {});
      l.coachTestSize = n;
      return s2;
    });
    showToast(ct("adaptive.hub.toast_size", "Tests are now {n} words", { n }));
  }

  function switchToManual() {
    update((s) => {
      const l = s.learningContent[child.id] || (s.learningContent[child.id] = {});
      if (game === "spelling") l.spellingAuto = { ...(l.spellingAuto || {}), list: v.band };
      else if (game === "vocab") l.vocabCategory = v.band;
      else if (game === "mathsworkout") l.mathsBand = v.band;
      return s;
    });
    setConfirmOpen(false);
    showToast(ct("adaptive.hub.toast_manual", "{game} switched to manual", { game: HUB_GAME_NAMES[game] }));
    if (onSwitchedToManual) onSwitchedToManual();
  }

  return (
    <div className="flex flex-col gap-3" data-testid="adaptive-game-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700" data-testid="adaptive-detail-level">{gameLevelText(game, v)}</span>
        {startedFrom && <span className="text-xs text-slate-500">{startedFrom}</span>}
        {game !== "ttstreaks" && v.active && (
          <button onClick={() => setConfirmOpen(true)} className="text-xs font-bold text-slate-900 underline underline-offset-2" data-testid="adaptive-detail-change">{ct("adaptive.hub.change", "Change")}</button>
        )}
      </div>
      {v.manual && (
        /* LB-291: the hand-back moved here from the summary row (Alan, 15 Sep). It sits in the
           same eyeline as the note further down that explains why the level is not moving,
           which is the context a parent should have BEFORE handing back, not after. */
        <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2.5" data-testid="adaptive-hand-back">
          <span className="text-xs text-slate-600 leading-snug">{ct("adaptive.hub.hand_back_sub", "Coach is standing back while you run this one.")}</span>
          <button onClick={() => onHandBack && onHandBack(childId, game)} className="text-xs font-bold text-slate-900 underline underline-offset-2 whitespace-nowrap flex-shrink-0" data-testid="adaptive-hand-back-btn">
            {ct("adaptive.hub.hand_back", "Hand back to Coach")}
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white rounded-lg px-3 py-2.5"><div className="text-xl font-extrabold text-slate-900">{testsThisWeek}</div><div className="text-xs text-slate-500">{ct("adaptive.hub.stat_tests_weeks", "tests this week · {n} last week", { n: testsLastWk })}</div></div>
        <div className="bg-white rounded-lg px-3 py-2.5"><div className="text-xl font-extrabold text-slate-900">{avg === null ? "–" : `${avg}%`}</div><div className="text-xs text-slate-500">{ct("adaptive.hub.stat_avg", "average score, last month")}</div></div>
        <div className="bg-white rounded-lg px-3 py-2.5"><div className="text-xl font-extrabold text-slate-900">{mastered}</div><div className="text-xs text-slate-500">{ct("adaptive.hub.stat_topics", "topics mastered")}</div></div>
        <div className="bg-white rounded-lg px-3 py-2.5"><div className="pt-1"><Sparkline points={points} tone={trendTone(points)} /></div><div className="text-xs text-slate-500">{ct("adaptive.hub.stat_trend", "scores, recent tests")}</div></div>
      </div>
      <div className="bg-white rounded-lg px-4 py-3">
        <div className="text-xs font-bold text-slate-900 mb-1.5">{ct("adaptive.hub.coming_up", "Coming up next")}</div>
        {upcoming.length ? (
          <div className="flex flex-wrap gap-1.5" data-testid="adaptive-detail-topics">{upcoming.map((t) => <span key={t} className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700">{t}</span>)}</div>
        ) : <p className="text-xs text-slate-500">{ct("adaptive.hub.coming_up_none", "Play a test and this fills in")}</p>}
        <p className="text-xs text-slate-400 mt-2">{ct(hasHistory ? "adaptive.hub.coming_up_note" : "adaptive.hub.coming_up_note_new", hasHistory ? "Chosen by Learning Coach from what {name} found hard recently" : "A starting point for {name}, based on their school year. This changes as they play.", { name: child.name })}</p>
      </div>
      {(game === "spelling" || game === "vocab") && !v.manual && (
        <div className="bg-white rounded-lg p-4 mb-3" data-testid="adaptive-test-size">
          <div className="text-xs font-bold text-slate-900 mb-1">{ct("adaptive.hub.size_title", "Words per test")}</div>
          <p className="text-xs text-slate-500 mb-2.5">{ct("adaptive.hub.size_note", "Longer tests move through a band faster. Shorter ones are easier to fit in.")}</p>
          <div className="flex items-center gap-2">
            <input type="number" inputMode="numeric" min={MIN_TEST_SIZE} max={MAX_TEST_SIZE} value={sizeDraft} data-testid="coach-size-input"
              aria-label={ct("adaptive.hub.size_title", "Words per test")}
              onChange={(e) => setSizeDraft(e.target.value.replace(/\D/g, ""))}
              onBlur={commitTestSize}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
              className="w-20 rounded-lg border border-slate-200 text-sm font-bold text-center py-2" />
            <span className="text-xs text-slate-400">{ct("adaptive.hub.size_range", "Between {min} and {max}", { min: MIN_TEST_SIZE, max: MAX_TEST_SIZE })}</span>
          </div>
          {solidWords > 0 && (
            <p className="text-xs text-slate-400 mt-2.5" data-testid="coach-solid-count">{ct("adaptive.hub.size_solid", "{n} words are coming up less often now, because they've been right twice on different days.", { n: solidWords })}</p>
          )}
        </div>
      )}
      <div className="bg-white rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid="adaptive-rewards-row">
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-bold text-slate-900">{ct("adaptive.hub.rewards_title", "Rewards")}</span>
          <span className="block text-xs text-slate-500 mt-0.5" data-testid="adaptive-rewards-line">{game === "ttstreaks"
            ? ct("adaptive.hub.rewards_line_tt", "streak of {n} pays {p} · perfect run pays {f}", { n: rw.streak, p: rw.pass, f: rw.full })
            : ct("adaptive.hub.rewards_line", "{p} for a pass ({t}%) · {f} for 100%", { p: rw.pass, t: rw.threshold, f: rw.full })}</span>
        </span>
        <button onClick={() => setRewardsOpen(true)} className="text-xs font-bold text-slate-900 underline underline-offset-2 flex-shrink-0" data-testid="adaptive-rewards-change">{ct("adaptive.hub.rewards_change", "View / change")}</button>
      </div>
      {/* LB-366: rows open the test and "See all" opens the history on this game. Until the
          game has a history record the card shows the Coach log exactly as before. */}
      <RecentTestsCard learning={learning} game={game} today={today} onOpenTest={onOpenTest ? (g, id) => onOpenTest(child.id, g, id) : null} onSeeAll={onSeeAllTests ? (g) => onSeeAllTests(child.id, g) : null} />
      {confirmOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-end" role="dialog" aria-label="Switch to manual" onClick={() => setConfirmOpen(false)}>
          <div className="bg-white rounded-t-2xl w-full max-w-lg mx-auto px-4 pt-5 pb-6" onClick={(e) => e.stopPropagation()} data-testid="adaptive-override-sheet">
            <h3 className="text-base font-extrabold text-slate-900">{ct("adaptive.hub.confirm_title", "Switch {game} to manual", { game: HUB_GAME_NAMES[game].toLowerCase() })}</h3>
            <p className="text-sm text-slate-500 mt-2">{ct("adaptive.hub.confirm_body", "You will choose {name}'s difficulty level and set the tests yourself. Learning Coach stops adjusting {game} and will not restart on its own. You can turn it back on here whenever you like.", { name: child.name, game: HUB_GAME_NAMES[game].toLowerCase() })}</p>
            <button onClick={switchToManual} className="w-full mt-4 bg-slate-900 text-white text-sm font-bold rounded-lg py-3" data-testid="adaptive-override-confirm">{ct("adaptive.hub.confirm_go", "Switch to manual")}</button>
            <button onClick={() => setConfirmOpen(false)} className="w-full mt-1 text-slate-900 text-sm font-bold py-2.5">{ct("adaptive.hub.confirm_keep", "Keep Learning Coach")}</button>
          </div>
        </div>,
        document.body
      )}
      {rewardsOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-end" role="dialog" aria-label="Rewards" onClick={() => setRewardsOpen(false)}>
          <div className="bg-white rounded-t-2xl w-full max-w-lg mx-auto px-4 pt-5 pb-6" onClick={(e) => e.stopPropagation()} data-testid="adaptive-rewards-sheet">
            <h3 className="text-base font-extrabold text-slate-900">{ct("adaptive.hub.rewards_sheet_title", "{game} rewards · {name}", { game: HUB_GAME_NAMES[game], name: child.name })}</h3>
            <p className="text-sm text-slate-500 mt-1.5">{ct("adaptive.hub.rewards_note", "These apply to every test, whether Coach picks the words or you do.")}</p>
            <div className="flex flex-col gap-3 mt-4">
              {game === "ttstreaks" ? (
                <>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs font-medium text-slate-600">{ct("adaptive.hub.rewards_streak_label", "Streak length")}</span><Stepper value={rw.streak} min={2} max={rw.size} label="Streak length" onCommit={(n) => setRewardField("ttStreakPassCount", n)} /></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs font-medium text-slate-600">{ct("adaptive.hub.rewards_streak_tokens", "Tokens for a streak")}</span><Stepper value={rw.pass} min={0} max={999} label="Tokens for a streak" onCommit={(n) => setRewardField("ttStreakPassReward", n)} /></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs font-medium text-slate-600">{ct("adaptive.hub.rewards_perfect_tokens", "Tokens for a perfect run")}</span><Stepper value={rw.full} min={1} max={999} label="Tokens for a perfect run" onCommit={(n) => setRewardField("ttStreakFullReward", n)} /></div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs font-medium text-slate-600">{ct("adaptive.hub.rewards_passmark_label", "Pass mark (%)")}</span><Stepper value={rw.threshold} min={1} max={99} label="Pass mark percent" onCommit={(n) => setRewardField(rw.thresholdKey, n)} /></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs font-medium text-slate-600">{ct("adaptive.hub.rewards_pass_tokens", "Tokens for a pass")}</span><Stepper value={rw.pass} min={0} max={999} label="Tokens for a pass" onCommit={(n) => setRewardField(rw.passKey, n)} /></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs font-medium text-slate-600">{ct("adaptive.hub.rewards_full_tokens", "Tokens for 100%")}</span><Stepper value={rw.full} min={1} max={999} label="Tokens for one hundred percent" onCommit={(n) => setRewardField(rw.fullKey, n)} /></div>
                </>
              )}
            </div>
            <button onClick={() => setRewardsOpen(false)} className="w-full mt-5 bg-slate-900 text-white text-sm font-bold rounded-lg py-3" data-testid="adaptive-rewards-done">{ct("adaptive.hub.rewards_done", "Done")}</button>
          </div>
        </div>
        , document.body
      )}
    </div>
  );
}

// ---- the hub shell ----------------------------------------------------------------------
// A full-screen page stack in the KidDetail-modal family: picker → summary → game detail,
// with Manual (LearningAdminTab) and the explainer as sibling pages. Back always goes up
// one level; the ✕ closes the whole hub.
// Nothing here should ever throw — but when it did (the duplicate-React defect in the first
// cut), the whole parent surface went white and the only person who could see it had no way
// to read the error. On a home-screen PWA there is no console to open, so a crash that says
// nothing is a crash we cannot fix. This turns it into a line of text you can photograph.
class HubErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="bg-white rounded-lg border border-rose-200 p-4 m-2" data-testid="hub-error">
        <div className="text-sm font-extrabold text-rose-700 mb-1">{ct("adaptive.hub.error_title", "Learn & Earn could not open")}</div>
        <p className="text-xs text-slate-600 mb-2">{ct("adaptive.hub.error_body", "Something went wrong drawing this screen. Send this line to whoever is fixing it:")}</p>
        <code className="block text-[11px] text-slate-800 bg-slate-50 rounded p-2 break-words">{String(this.state.err && (this.state.err.message || this.state.err))}</code>
      </div>
    );
  }
}

export function LearnEarnHub({ state, update, showToast, onClose, initialChildId = null, startOnSetHomework = null }) {
  const householdOn = coachEnabled(state.settings);
  // With a child supplied (the Kids-tab route) the hub opens on that child's summary and
  // never shows the picker; from Parent Home it opens on the picker as before.
  const coachOn = coachEnabled(state.settings);
  // Parent Home's Set homework tile (Alan, 2 Oct) opens the hub on step one of Set homework
  // for one child, with that child's Homework practice page beneath it, so Back from step one
  // goes where it goes when Set homework is opened the long way. A child with no room left
  // (grid and drafts full) lands on the Homework practice page instead, which says which limit
  // is in the way: the same answer the page's own Set homework button gives. A household for
  // whom homework is locked (Premium, 2 Oct) lands there too, on the placeholder line.
  const [stack, setStack] = useState(() => {
    if (startOnSetHomework) {
      const id = startOnSetHomework;
      const room = canAddList((state.learningContent || {})[id], todayStr()) && homeworkUnlocked(state.settings);
      return room ? [{ page: "homework", childId: id }, { page: "homework-new", childId: id }] : [{ page: "homework", childId: id }];
    }
    return initialChildId
      ? [{ page: coachOn ? "summary" : "manual", childId: initialChildId }]
      : [{ page: "picker" }];
  });
  const embeddedInRow = !!initialChildId;
  const [manualDetailOpen, setManualDetailOpen] = useState(false);
  const top = stack[stack.length - 1];
  const hubRef = useRef(null);
  // Every move inside the hub starts at the top of the hub. A browser keeps its scroll
  // offset across an in-place navigation, so a page shorter than the offset renders as an
  // empty screen — scrollMarginTop clears the sticky masthead and the notch.
  const toTop = () => { if (hubRef.current) setTimeout(() => hubRef.current && hubRef.current.scrollIntoView({ behavior: "auto", block: "start" }), 0); };
  const push = (frame) => { setStack((s) => [...s, frame]); toTop(); };
  const pop = () => { setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)); toTop(); };
  // Alan, 27 Sep: inside Set homework the link at the top goes back ONE step ("Back to step 1"),
  // not out of the flow. HomeworkCreate reports { label, go } through onNav, or null on step 1.
  const [createNav, setCreateNav] = useState(null);
  // LB-315: each frame above the first is one back entry (back walks the way the parent came
  // in, as the on-screen Back does); inside Set homework, steps 2 and 3 are one more.
  useBackDepth(stack.length - 1, pop);
  useBackLayer(!!createNav, () => { if (createNav) createNav.go(); });
  // LB-366: the history (optionally filtered to one game) and one test, as pages of this
  // same stack, so Back always walks the way the parent came in.
  const openHistory = (childId, game = null) => push({ page: "history", childId, game });
  const openTest = (childId, game, id) => push({ page: "test", childId, game, id });
  const child = top.childId ? (state.profiles || []).find((p) => p.id === top.childId) : null;

  function backToAdaptive(childId, game) {
    update((s) => {
      const l = s.learningContent[childId] || (s.learningContent[childId] = {});
      if (game === "spelling") l.spellingAuto = { ...(l.spellingAuto || {}), list: "auto" };
      else if (game === "vocab") l.vocabCategory = "auto";
      else if (game === "mathsworkout") l.mathsBand = "auto";
      return s;
    });
    showToast(ct("adaptive.hub.toast_back", "{game} is back with Learning Coach", { game: HUB_GAME_NAMES[game] }));
  }

  function pageTitle(frame) {
    if (!frame) return ct("adaptive.hub.title", "Learn & Earn");
    const c = frame.childId ? (state.profiles || []).find((p) => p.id === frame.childId) : null;
    if (frame.page === "picker") return ct("adaptive.hub.title", "Learn & Earn");
    if (frame.page === "summary") return ct("adaptive.hub.summary_title", "Coach's summary");
    if (frame.page === "game") return HUB_GAME_NAMES[frame.game];
    if (frame.page === "history") return c ? ct("history.parent.title", "{name}'s test history", { name: c.name }) : ct("history.parent.entry_title", "Test history");
    if (frame.page === "test") return historyGameName(frame.game);
    if (frame.page === "manual") return c ? ct("adaptive.hub.manual_title", "{name}'s Learn & Earn", { name: c.name }) : ct("adaptive.hub.title", "Learn & Earn");
    if (frame.page === "homework") return ct("homework.row.title", "Homework practice");
    if (frame.page === "homework-new") return ct("homework.create.title", "Set homework");
    if (frame.page === "homework-list" || frame.page === "homework-edit") { const l = c ? findHomeworkList((state.learningContent || {})[c.id], frame.code) : null; return l ? l.name : ct("homework.row.title", "Homework practice"); }
    return ct("adaptive.hub.explainer_title", "Learning Coach");
  }

  const title = top.page === "picker" ? ct("adaptive.hub.title", "Learn & Earn")
    : top.page === "summary" ? ct("adaptive.hub.summary_title", "Coach's summary")
    : top.page === "game" ? `${HUB_GAME_NAMES[top.game]} · ${child ? child.name : ""}`
    : top.page === "manual" ? (child ? ct("adaptive.hub.manual_title", "{name}'s Learn & Earn", { name: child.name }) : "Learn & Earn")
    : top.page === "history" ? (child ? ct("history.parent.title", "{name}'s test history", { name: child.name }) : ct("history.parent.entry_title", "Test history"))
    : top.page === "test" ? historyGameName(top.game)
    : top.page === "homework" ? (child ? ct("homework.page.title", "Homework practice · {name}", { name: child.name }) : ct("homework.row.title", "Homework practice"))
    : top.page === "homework-new" ? (child ? ct("homework.create.title_named", "Set homework · {name}", { name: child.name }) : ct("homework.create.title", "Set homework"))
    : top.page === "homework-list" || top.page === "homework-edit" ? (() => { const l = child ? findHomeworkList((state.learningContent || {})[child.id], top.code) : null; return l ? `${l.name}${child ? ` · ${child.name}` : ""}` : ct("homework.row.title", "Homework practice"); })()
    : ct("adaptive.hub.explainer_title", "Learning Coach");

  return (
    <div data-testid="le-hub" ref={hubRef} className="pb-[max(28px,calc(env(safe-area-inset-bottom,0px)+20px))]" style={{ scrollMarginTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
      <HubErrorBoundary>
      <div className="w-full pb-4 lb-form">
        {(stack.length > 1 || !embeddedInRow) && !manualDetailOpen && (
        <button onClick={top.page === "homework-new" && createNav ? createNav.go : stack.length > 1 ? pop : onClose} className="text-xs font-bold text-slate-500 mb-3 flex items-center" data-testid="le-hub-back">
          <Icon id="ic-back" size={12} className="inline-block align-[-1px] mr-1" style={{ color: "currentColor" }} />
          {top.page === "homework-new" && createNav ? createNav.label : ct("settings.back_to", "Back to {tab}", { tab: stack.length > 1 ? pageTitle(stack[stack.length - 2]) : "Home" })}
        </button>
        )}
        <h2 className="text-xl font-extrabold text-slate-900 mb-1">{title}</h2>
        {top.page === "picker" && <p className="text-xs text-slate-500 mb-3">{ct("adaptive.hub.picker_sub", "Choose a child")}</p>}
        {top.page === "summary" && <p className="text-xs mb-3"><span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">{ct("adaptive.hub.premium", "Premium · free in the Preview")}</span></p>}

        {top.page === "picker" && (
          <LearnEarnChildPicker
            state={state}
            onOpenChild={(childId) => push({ page: "summary", childId })}
            onOpenManual={(childId) => push({ page: "manual", childId })}
            onOpenExplainer={() => push({ page: "explainer" })}
          />
        )}
        {top.page === "summary" && (
          <>
          <AdaptiveSummaryPage
            state={state}
            childId={top.childId}
            onSwitchChild={(childId) => setStack((s) => [...s.slice(0, -1), { page: "summary", childId }])}
            onOpenGame={(childId, game) => push({ page: "game", childId, game })}
            onBackToAdaptive={backToAdaptive}
            onOpenHistory={openHistory}
            hideSwitcher={!!initialChildId}
            homeworkRow={<HomeworkRow state={state} childId={top.childId} onOpen={() => push({ page: "homework", childId: top.childId })} onNew={() => push({ page: "homework-new", childId: top.childId })} />}
          />
          <button
            onClick={() => push({ page: "manual", childId: top.childId })}
            className="text-xs font-bold text-slate-900 text-left mt-2 px-1"
            data-testid="kd-open-manual"
          >{ct("adaptive.hub.open_manual", "Set it yourself for {name}", { name: (state.profiles || []).find((p) => p.id === top.childId)?.name || "this child" })}</button>
          </>
        )}
        {top.page === "game" && (
          <>
            <AdaptiveGameDetail state={state} update={update} childId={top.childId} game={top.game} showToast={showToast} onHandBack={backToAdaptive} onSwitchedToManual={() => setStack((s) => [...s.slice(0, -1), { page: "manual", childId: top.childId }])} onOpenTest={openTest} onSeeAllTests={openHistory} />
            <button onClick={() => push({ page: "manual", childId: top.childId })} className="text-xs font-bold text-slate-900 text-left mt-3 px-1" data-testid="adaptive-open-manual">{ct("adaptive.hub.open_manual", "Set it yourself for {name}", { name: child ? child.name : "this child" })}</button>
          </>
        )}
        {top.page === "manual" && child && !coachOn && (
          <div className="mb-3"><CoachAdvert onOpen={() => push({ page: "explainer" })} /></div>
        )}
        {top.page === "manual" && child && !manualDetailOpen && (
          <div className="mb-3"><TestHistoryEntry childName={child.name} onOpen={() => openHistory(child.id)} /></div>
        )}
        {/* LB-364: a household without Coach has no summary page, so the row lives here. */}
        {top.page === "manual" && child && !coachOn && !manualDetailOpen && (
          <HomeworkRow state={state} childId={child.id} onOpen={() => push({ page: "homework", childId: child.id })} onNew={() => push({ page: "homework-new", childId: child.id })} />
        )}
        {top.page === "manual" && child && (
          <LearningAdminTab state={state} update={update} activeChild={child} showToast={showToast} hideFun embedded onDetailOpen={setManualDetailOpen} onOpenTest={(g, id) => openTest(child.id, g, id)} onSeeAllTests={(g) => openHistory(child.id, g)} />
        )}
        {top.page === "history" && child && (
          <ParentTestHistory key={`${child.id}:${top.game || "all"}`} history={((state.learningContent || {})[child.id] || {}).testHistory || {}} requests={state.requests || []} initialGame={top.game || null} onOpenTest={(g, id) => openTest(child.id, g, id)} />
        )}
        {top.page === "homework" && child && (
          <HomeworkPage state={state} update={update} showToast={showToast} childId={child.id} onNew={() => push({ page: "homework-new", childId: child.id })} onOpenList={(code) => push({ page: "homework-list", childId: child.id, code })} onSeeHistory={() => openHistory(child.id, "homework")} />
        )}
        {top.page === "homework-new" && child && (
          <HomeworkCreate state={state} update={update} childId={child.id} showToast={showToast} onNav={setCreateNav} onCancel={pop} onDone={(code) => setStack((s) => [...s.slice(0, -1), { page: "homework-list", childId: child.id, code }])} />
        )}
        {top.page === "homework-list" && child && (
          <HomeworkListPage state={state} update={update} childId={child.id} code={top.code} showToast={showToast} onEdit={() => push({ page: "homework-edit", childId: child.id, code: top.code })} onRemoved={pop} />
        )}
        {top.page === "homework-edit" && child && (
          <HomeworkCreate state={state} update={update} childId={child.id} showToast={showToast} editCode={top.code} onCancel={pop} onDone={pop} />
        )}
        {top.page === "test" && child && (
          <ParentTestDetail history={((state.learningContent || {})[child.id] || {}).testHistory || {}} requests={state.requests || []} game={top.game} id={top.id} childName={child.name} />
        )}
        {top.page === "explainer" && (
          <div className="rounded-xl px-4 py-4 text-white" style={{ background: "#1B2A4A" }} data-testid="adaptive-explainer">
            <div className="flex items-center gap-2 text-sm font-extrabold">{ct("adaptive.explainer.title", "Learning Coach")}</div>
            <p className="text-xs text-white/90 mt-2">{ct("adaptive.explainer.lede", "Two children in the same year aren't necessarily on the same level, and no child is on the same level in every subject.")}</p>

            <div className="text-[11px] font-extrabold uppercase tracking-wide text-white/60 mt-4 mb-1.5">{ct("adaptive.explainer.how_title", "How it works")}</div>
            <ol className="list-none p-0 m-0 space-y-2">
              {[
                ct("adaptive.explainer.how_1", "**It starts from their school year.** Every game opens at the level a child their age is working at, so nothing is a shock on day one."),
                ct("adaptive.explainer.how_2", "**It finds their level inside that year.** There are five levels within a school year. Learning Coach works out which one fits each game and keeps them there while they settle."),
                ct("adaptive.explainer.how_3", "**It moves when they have earned it.** A run of strong tests moves them up. A hard week eases off and brings the tricky topics back until they stick. Nothing is taken away for a bad week."),
              ].map((line, i) => (
                <li key={i} className="text-xs text-white/90 flex gap-2">
                  <span className="font-extrabold text-white/60 flex-shrink-0">{i + 1}</span>
                  <span>{line.split("**").map((part, n) => (n % 2 ? <b key={n} className="text-white">{part}</b> : part))}</span>
                </li>
              ))}
            </ol>

            <div className="text-[11px] font-extrabold uppercase tracking-wide text-white/60 mt-4 mb-1.5">{ct("adaptive.explainer.why_title", "Why it beats doing it yourself")}</div>
            <ul className="list-none p-0 m-0 space-y-2">
              {[
                ct("adaptive.explainer.why_1", "**You stop being the administrator.** No lists to choose, no levels to guess at, no wondering whether last week was too easy."),
                ct("adaptive.explainer.why_2", "**Each subject moves on its own.** A child can be flying in maths and finding spelling hard. Learning Coach treats them as two different things, because they are."),
                ct("adaptive.explainer.why_3", "**You can still overrule it.** Set any game yourself whenever you like, and Learning Coach steps back for that one until you hand it over again."),
              ].map((line, i) => (
                <li key={i} className="text-xs text-white/90 flex gap-2">
                  <span className="text-white/60 flex-shrink-0">&#8226;</span>
                  <span>{line.split("**").map((part, n) => (n % 2 ? <b key={n} className="text-white">{part}</b> : part))}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-white/80 mt-4">{ct("adaptive.explainer.summary_line", "You get a weekly summary in plain English: what they played, how it went, what is coming next, and whether they moved up.")}</p>

            {/* Activation happens HERE. Sending a parent to Settings to find a switch was the
                step that lost them, and this is where an upgrade flow will sit later. */}
            <button
              type="button"
              data-testid="adaptive-explainer-activate"
              className="w-full bg-white text-slate-900 font-extrabold text-sm rounded-lg py-2.5 mt-4"
              onClick={() => {
                update((s2) => { s2.settings.adaptive = true; return s2; });
                if (showToast) showToast(ct("toast.adaptive_on", "Learning Coach is on for every child."), "good");
                setStack([{ page: "picker" }]);
              }}
            >{ct("adaptive.explainer.activate", "Turn on Learning Coach")}</button>
            <p className="text-[11px] text-white/70 mt-2 text-center">{ct("adaptive.explainer.activate_note", "You can turn it off at any time, and nothing your children have earned is affected.")}</p>
          </div>
        )}
      </div>
      </HubErrorBoundary>
    </div>
  );
}

// ---- the Kids-tab pane (Alan, staging pass: the Learn & Earn row shows the ADAPTIVE view
// for an Adaptive child, not the Manual one) -----------------------------------------------
// Embedded in the row: the child's Adaptive summary first, per-game detail behind each row,
// and the Manual view one deliberate tap away. A fully Manual child skips straight to
// LearningAdminTab — KidDetail decides which to mount.
export function ChildLearnEarnPane({ state, update, showToast, child }) {
  // One journey, two entry points. This used to be its own stack with its own headers and
  // back controls, which is why the Kids-tab route looked different from the Parent Home one
  // and ended up with nested titles. It now runs the same hub with the child pre-selected.
  return (
    <div data-testid="kd-le-pane">
      <LearnEarnHub state={state} update={update} showToast={showToast} initialChildId={child.id} />
    </div>
  );
}
